/* =============================================================================
 * The dirham sign
 * -----------------------------------------------------------------------------
 * The UAE's new symbol for its currency. It replaces the letters AED wherever a
 * person reads an amount — on screen, on a payslip, on a settlement — and it
 * replaces them nowhere a machine reads one.
 *
 * -----------------------------------------------------------------------------
 * WHY IT IS A PRIVATE-USE CHARACTER
 * -----------------------------------------------------------------------------
 * The symbol is new. No font on any phone, laptop or printer in this company
 * has a glyph for it yet, so a standard codepoint would render as an empty box
 * on every screen — which is worse than the letters it replaced, because a box
 * is not a unit at all.
 *
 * So it travels as a character in a font of our own: public/fonts/noor-dirham.ttf,
 * one glyph, built by scripts/build-dirham-font.py. The browser picks it up
 * through an @font-face with `unicode-range: U+E900`, which means the face is
 * consulted for this one character and skipped for every other — so putting it
 * first in a font stack costs nothing and changes no other letter on the page.
 * The PDFs embed the same file.
 *
 * A character, rather than a picture, because an amount is a *string*:
 * formatMoney returns one, forty call sites render it, a screen reader reads it
 * out, and a PDF draws it in the middle of a line. A picture would mean
 * changing every one of those and would still not work inside a text run.
 *
 * -----------------------------------------------------------------------------
 * THE OUTLINE IS THE BANK'S, AND SO ARE THE RULES ROUND IT
 * -----------------------------------------------------------------------------
 * The glyph is cut from scripts/dirham-symbol.svg — the Central Bank of the
 * UAE's published artwork, kept in the repository as it was downloaded. The
 * guideline that came with it says the mark may not be redrawn or
 * re-proportioned, and the first version of this font was exactly that: a
 * slab-serif D drawn here from a description of the symbol.
 *
 * The rest of the guideline is about how it is set, and those rules are kept in
 * the places that can keep them rather than in a document nobody opens:
 *
 *   as tall as the figures beside it, on their baseline — cut into the glyph
 *   (720 units, where Helvetica's and Poppins' figures stand) and stood down
 *   to Cairo's on the Arabic pages by a second @font-face in app.css
 *
 *   to the left of the numeral, with space between — formatMoney and the
 *   documents put it there, and dirhamPdf keeps it there when an Arabic line
 *   is laid out the other way
 *
 *   never the sign and the letters AED together — which is the line below
 *   about where the letters stay: a document has one or the other, never both
 *
 *   no smaller than 12px on screen or 5pt in print, because under that the two
 *   bars close up and the mark reads as a letter D
 *
 *   never as a logo, never on a splash screen, and never in place of the word
 *   "Dirhams" where an amount is spelled out — see reports/words.ts, which
 *   writes the word
 *
 * theDirhamHasItsOwnSign.test.ts holds each of those to the file it lives in.
 *
 * -----------------------------------------------------------------------------
 * WHERE THE LETTERS STAY
 * -----------------------------------------------------------------------------
 * AED is an ISO 4217 code before it is a word, and three places read it as one:
 *
 *   the WPS file a bank ingests, and the MOHRE filings beside it
 *   companies.currency, which is the stored code every one of those is built
 *   from
 *   a spreadsheet, whose reader does not have this font installed and would
 *   get a box in a column of figures
 *
 * None of those are a person reading an amount. All of them keep AED.
 * ========================================================================== */

/** The character. Private use, until the standard catches up with the country. */
export const DIRHAM_SIGN = '';

/** The family name the font is registered under, in the browser and in a PDF. */
export const DIRHAM_FONT = 'NoorOne Dirham';

/** Where the browser and jsPDF both fetch it from. */
export const DIRHAM_FONT_URL = '/fonts/noor-dirham.ttf';

/**
 * The currency this symbol belongs to.
 *
 * A company set to rupees or dollars keeps its own sign; this is the dirham's,
 * and applying it to anything else would be relabelling somebody's money.
 */
export function isDirham(code: string | null | undefined): boolean {
  return (code ?? '').trim().toUpperCase() === 'AED';
}

/** Whether a piece of text carries the sign and so needs the font. */
export function hasDirham(text: string): boolean {
  return text.includes(DIRHAM_SIGN);
}

/**
 * The sign put back into letters.
 *
 * For anywhere the glyph cannot be drawn — a spreadsheet, a bank file, a plain
 * text email. Never silently: a unit that has gone missing is worse than one
 * spelled out, so anything that cannot draw the symbol writes the code instead.
 */
export function spellDirham(text: string): string {
  return text.split(DIRHAM_SIGN).join('AED');
}
